import {FetchPolicy, gql} from '@apollo/client';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible, makeInaccessibleInfoGQL} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {ColumnGroup, StudiesColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {LiveCellValue} from 'notebook/structured-tables/LiveCellValues';
import {
  ColumnAdapter,
  GetColumnAdapterCustomEvalArgsOptions,
} from 'notebook/structured-tables/StructuredTableColumnAdapters';
import {
  EvalTableView,
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EphemeralObjectSpec, toCoreObject} from 'notebook/structured-tables/util/EphemeralObjectSpec';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import updateCellWithLiveValue from 'notebook/structured-tables/util/updateCellWithLiveValue';
import {CellIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {CellError, LoadingError} from 'notebook/tables/excess/Util';
import {
  CustomResolutionQueryType,
  ServerResolutionResult,
  StudiesColumnQuery,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {
  CustomEvalFnAndType,
  CustomEvalResult,
  CustomEvalType,
  TableColumnType,
} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  coerceToPrimitive,
  isTypedValueEmpty,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {StudiesTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {isItemStudyable, isItemTypeStudyable} from 'studies/studyable/StudyableUtils';
import APIUtil from 'util/APIUtil';
import sortedBy from 'util/sortedBy';

import {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {StudyableTypedValueDataGraphQL} from '../../../graphql-fragments/studies/__generated/StudyableTypedValueDataGraphQL';
import {
  StudyableLiveDataQuery,
  StudyableLiveDataQuery_studyableItems as StudyableItems,
  StudyableLiveDataQueryVariables,
} from './__generated/StudyableLiveDataQuery';

export const NOT_STUDYABLE_CELL_ERROR = 'Item cannot be associated with studies! Clear value.';

export function makeStudyTypedValue(studies: StudyableTypedValueDataGraphQL['studies']): StudiesTypedValue {
  // Keep in-sync with benchling/notebook/tables/typed_value.py:_get_studies_typed_value
  return {
    type: BaseTypedValueType.STUDIES,
    studies: sortedBy(
      studies.map((study) =>
        !isInaccessible(study)
          ? {
              id: study.id,
              name: study.name,
              humanId: study.humanId,
            }
          : {
              id: study.invalidId,
              name: '',
              humanId: 'No access',
            }
      ),
      [{naturalSort: true, accessor: 'humanId'}]
    ),
  };
}

export function getStudyIdsFromTypedValue(typedValue: TypedValue): Array<string> {
  if (typedValue.type !== BaseTypedValueType.STUDIES) {
    return [];
  } else {
    return typedValue.studies.map((study) => study.id);
  }
}

function isStudyableSupportedForObject(sampleLookupInfo: SampleLookupInfo): boolean {
  const item = {id: sampleLookupInfo.idForLookup};
  // There may be cases where the item type is studyable, but the specific instance is not.
  // For example, containers are studyable but well plate wells (also containers) are not.
  // In this case, the read path will return an empty set of studies, and the write path will error.
  // If the item type is studyable, we preemptively enable the studies column,
  // and then lock the cell (to prevent writes) using the custom eval function if the specific instance is not.
  return isItemTypeStudyable(item);
}

export const STUDYABLE_LIVE_DATA_QUERY = gql`
  query StudyableLiveDataQuery($studyableIds: [StrictString]!) @skipAudit @engComponent(name: STUDIES) {
    studyableItems(ids: $studyableIds) {
      ...InaccessibleInfoGraphQL
      ...StudyableTypedValueDataGraphQL
    }
  }
  ${getFragmentWrapper('StudyableTypedValueData').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const StudyableRowUpdater: StructuredTableRowUpdater<StudyableTypedValueDataGraphQL, unknown> = {
  updateRow<TRowMetadata>(
    model: StudyableTypedValueDataGraphQL | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<TRowMetadata>,
    columnGroup: ColumnGroup
  ): StructuredTableRow<TRowMetadata> {
    if (model == null || isInaccessible(model)) {
      return row;
    }
    const cells = row.cells.map((cell, columnIndex) => {
      const column = columns[columnIndex];
      const liveValue = this.updateCell(model, columnGroup, column);
      return updateCellWithLiveValue(cell, liveValue);
    });
    return {...row, cells};
  },

  updateCell(
    model: StudyableTypedValueDataGraphQL | InaccessibleInfoGraphQL,
    columnGroup: ColumnGroup,
    column: StructuredTableColumn
  ): LiveCellValue {
    if (
      (column.columnGroup ?? null) !== columnGroup ||
      column.columnType !== TableColumnType.STUDIES ||
      model == null ||
      isInaccessible(model)
    ) {
      return 'NO_VALUE';
    }
    return makeStudyTypedValue(model.studies);
  },

  async fetchModel(
    sampleLookupInfo: SampleLookupInfo
  ): Promise<StudyableTypedValueDataGraphQL | InaccessibleInfoGraphQL> {
    if (!isStudyableSupportedForObject(sampleLookupInfo)) {
      return makeInaccessibleInfoGQL(sampleLookupInfo.idForLookup);
    }
    const id = sampleLookupInfo.idForLookup;
    return await fetchStudyable(id, 'network-only');
  },

  async bulkFetchModel(sampleLookupInfos: Array<SampleLookupInfo>): Promise<Array<StudyableItems>> {
    const filteredInfos = sampleLookupInfos.filter((info) => isStudyableSupportedForObject(info));
    if (filteredInfos.length) {
      const {studyableItems} = await doQuerySafe<StudyableLiveDataQuery, StudyableLiveDataQueryVariables>(
        {query: STUDYABLE_LIVE_DATA_QUERY},
        {
          variables: {
            studyableIds: filteredInfos.map((info) => info.idForLookup),
          },
          fetchPolicy: 'network-only',
        }
      );
      return studyableItems;
    } else {
      return [];
    }
  },

  readFromCache(sampleLookupInfo: SampleLookupInfo): StudyableTypedValueDataGraphQL | null {
    const id = sampleLookupInfo.idForLookup;
    if (!isStudyableSupportedForObject(sampleLookupInfo)) {
      return null;
    }
    const data = readQuerySafe<StudyableLiveDataQuery, StudyableLiveDataQueryVariables>(
      {query: STUDYABLE_LIVE_DATA_QUERY},
      {
        variables: {
          studyableIds: [id],
        },
      }
    );
    if (data == null) {
      return null;
    }
    assert(data.studyableItems.length === 1, 'Expected to return exactly one studyable item');
    const studyableItem = data.studyableItems[0];
    if (isInaccessible(studyableItem)) {
      return null;
    }
    return studyableItem;
  },
};

export const StudyableColumnAdapter: ColumnAdapter<TableColumnType.STUDIES> = {
  columnType: TableColumnType.STUDIES,
  rowUpdater: StudyableRowUpdater,
  doesFinalize: false,
  isUserConfigurable: true,
  isEnabled() {
    return BenchlingConfig.STUDIES_ENABLE;
  },
  // If the structured table is embedded in a worksheet execution step that's part of a PD study,
  // all the objects created in that table will be associated with the study when the table is submitted.
  // Allowing user edits in the studies column before submission presents a number of UX challenges,
  // so we hide the column in worksheets. The tables in a worksheet step can be defined in a procedure
  // template, so we need to hide the studies column there, too.
  //
  // Right now, for M1, procedures and worksheets are only created in the context of a PD study.
  // Additional logic may be required if this changes in the future, if they can be created independently.
  //
  // Keep in sync with benchling/studies/studyable/structured_table_action_function_context.py
  supports(
    objectSpec: EphemeralObjectSpec,
    structuredTableInsertionContext: StructuredTableInsertionContextValue
  ) {
    if (
      structuredTableInsertionContext.itemInsertedFromId != null &&
      (APIUtil.isWorksheet(structuredTableInsertionContext.itemInsertedFromId) ||
        APIUtil.isProcedure(structuredTableInsertionContext.itemInsertedFromId))
    ) {
      return false;
    }
    return isItemStudyable(toCoreObject(objectSpec));
  },
  makeColumn(columnGroup: ColumnGroup) {
    return makeStructuredTableColumn({
      columnType: TableColumnType.STUDIES,
      columnGroup,
      name: 'Studies',
    });
  },
  getCustomEvalFn(
    tableView: EvalTableView,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<{tableMetadata: unknown; rowMetadata: unknown; fileId?: string | null}> | null {
    return {
      customEvalFn: studiesColumnCustomEval,
      customEvalType: CustomEvalType.DEFAULT,
    };
  },

  getCustomEvalArgs(
    tableView: EvalTableView,
    cellIndex: CellIndex,
    options: GetColumnAdapterCustomEvalArgsOptions
  ): Array<string> {
    const {toSpreadsheetIndex, coreObjectTypes} = options;
    const {columns} = tableView;
    const {row: currentRowIndex, column: currentColumnIndex} = cellIndex;
    const currentColumn = columns[currentColumnIndex];
    const studyableTypes = coreObjectTypes.filter(
      (coreObjectType) =>
        isItemStudyable(toCoreObject(coreObjectType.objectSpec)) &&
        coreObjectType.columnGroup === (currentColumn.columnGroup ?? null)
    );
    const dependencies: Array<string> = [];
    for (const {columnType, columnGroup} of studyableTypes) {
      const studyableColumnIdx = columns.findIndex(
        (column) => column.columnType === columnType && (column?.columnGroup ?? null) === columnGroup
      );
      // There are cases (like the process task table), where the studyable column may not be present.
      // In these cases, we should not add a dependency.
      // The custom eval will short circuit and just return the original value, if there are no dependencies.
      if (studyableColumnIdx === -1) {
        continue;
      }
      dependencies.push(`=${toSpreadsheetIndex({row: currentRowIndex, column: studyableColumnIdx})}`);
    }
    assertAndContinue(dependencies.length <= 1, 'Expected at most one studyable column dependency.');
    return dependencies.slice(0, 1);
  },
};

/**
 * Resolves the given id to a STUDIES typed value if the object is studyable + accessible, otherwise null.
 */
export async function autopopulateStudies(query: StudiesColumnQuery): Promise<ServerResolutionResult> {
  const {studyableId} = query;
  const studyable = await fetchStudyable(studyableId, 'no-cache');
  if (studyable == null || isInaccessible(studyable)) {
    return makeCustomResolutionResultFromTypedValue(null, undefined);
  }
  return makeCustomResolutionResultFromTypedValue(
    studyable.isStudyable ? makeStudyTypedValue(studyable.studies) : null,
    undefined
  );
}

/**
 * This custom eval function will update the studies cell to show the studies associated with the
 * studyable item instance in each table row. User input takes precedence, so the studies will only be updated
 * after the item is selected, but before the user selects studies.
 * If the item type is studyable, but the specific instance is not, the cell will be locked to prevent
 * further input. If the user already selected studies prior to selecting an item that is not studyable,
 * an error is shown.
 */
function studiesColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: StudiesColumnDefinition,
  cellDependencies: [TypedValue | null],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  // The value of the cell used to select the studyable instance should be passed in as a dependency.
  const studyableId =
    cellDependencies.length > 0 && cellDependencies[0] != null
      ? coerceToPrimitive(cellDependencies[0], {shouldUseIds: true})
      : null;

  // There may be cases (like certain configurations of the process task table, or entity creation tables),
  // where the studyable does not exist or the studyable column is not present.
  // In these cases, we just return the original value.
  if (typeof studyableId !== 'string' || studyableId.length === 0) {
    // If the studyable id cannot be found, we return the original value.
    return {val: typedValue, error: null, locked: false};
  }

  // During workflows like spreadsheet imports, the user input may fail to match with a concrete
  // studyable instance. Instead, the studyable item selection cell may contain something like an invalid
  // barcode. If no studies have been selected yet, we should lock the cell to prevent further input.
  // If studies have been selected, we should show an error.
  if (!isItemTypeStudyable({id: studyableId})) {
    if (isTypedValueEmpty(typedValue)) {
      return {val: typedValue, error: null, locked: true};
    } else {
      return {val: typedValue, error: new CellError(NOT_STUDYABLE_CELL_ERROR), locked: false};
    }
  }

  // Query for the studies associated with the studyable instance.
  // If the item instance is not studyable, or it is inaccessible, an empty string value is returned.
  // If the item instance is studyable, a studies value is returned (even if there are no studies associated
  // with the item).
  const {resolvedValue, error} = resolveFromResolutionQuery(
    // resolveFromResolutionQuery will cache query results. This query will call autopopulate studies,
    // which will return null if the item instance is not studyable. If the query returns null, the typed value
    // here will then be returned as a fallback value.
    // If an invalid user input is passed as the typedValue here, (like studies for an item instance
    // that is not studyable), that invalid input will be cached as the final result if the query returns null.
    // That causes a problem, later, when the invalid input is removed. The query will run again once with
    // empty user input, but the resolution query will still return the invalid, previously cached value.
    // To prevent this, we provide an empty string value as the fallback.
    toStringTypedValue(''),
    {
      customResolutionQueryType: CustomResolutionQueryType.STUDIES_COLUMN,
      studyableId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  const isLoading = error?.type === LoadingError.type;
  const isStudyable = resolvedValue.type === BaseTypedValueType.STUDIES;

  // If a value has already been filled in, preserve the users input.
  // If the user selected studies, and then later selected an item that is not studyable, show an error.
  // isStudyable will evaluate to false if the query is loading so we don't show an error in that case.
  if (!isLoading && (typedValue.type === BaseTypedValueType.STUDIES || !isTypedValueEmpty(typedValue))) {
    return {
      val: typedValue,
      error: isStudyable ? error : new CellError(NOT_STUDYABLE_CELL_ERROR),
      locked: false,
    };
  }

  // Otherwise, populate the cell with the studies associated with the item.
  // If the item is not studyable, lock the cell to prevent further input.
  return {val: resolvedValue, error, locked: !isStudyable || isLoading};
}

async function fetchStudyable(
  id: string,
  fetchPolicy: FetchPolicy
): Promise<StudyableTypedValueDataGraphQL | InaccessibleInfoGraphQL> {
  const {studyableItems} = await doQuerySafe<StudyableLiveDataQuery, StudyableLiveDataQueryVariables>(
    {query: STUDYABLE_LIVE_DATA_QUERY},
    {
      variables: {
        studyableIds: [id],
      },
      fetchPolicy,
    }
  );
  assert(studyableItems.length === 1, 'Expected to return exactly one studyable item.');
  return studyableItems[0];
}
